/**
 * Utility functions for exporting data to CSV and Excel
 */
import ExcelJS from 'exceljs';

export interface ExportColumn<T> {
  key: keyof T | string;
  header: string;
  formatter?: (value: any, row: T) => string;
}

/**
 * Get value from nested key path
 */
const getNestedValue = <T extends Record<string, any>>(row: T, key: string): any => {
  if (key.includes('.')) {
    const keys = key.split('.');
    return keys.reduce((obj, k) => obj?.[k], row as any);
  }
  return row[key as keyof T];
};

/**
 * Prepare data for export with formatted values
 */
const prepareExportData = <T extends Record<string, any>>(
  data: T[],
  columns: ExportColumn<T>[]
): Record<string, any>[] => {
  return data.map(row => {
    const exportRow: Record<string, any> = {};
    columns.forEach(col => {
      let value = getNestedValue(row, col.key as string);
      if (col.formatter) {
        value = col.formatter(value, row);
      }
      exportRow[col.header] = value ?? '';
    });
    return exportRow;
  });
};

/**
 * Export data to CSV file with automatic download
 */
export const exportToCSV = <T extends Record<string, any>>(
  data: T[],
  columns: ExportColumn<T>[],
  filename: string
): void => {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  const headers = columns.map(col => `"${col.header}"`).join(',');

  const rows = data.map(row => {
    return columns.map(col => {
      let value = getNestedValue(row, col.key as string);
      if (col.formatter) {
        value = col.formatter(value, row);
      }
      if (value === null || value === undefined) {
        value = '';
      }
      const stringValue = String(value).replace(/"/g, '""');
      return `"${stringValue}"`;
    }).join(',');
  });

  const csvContent = [headers, ...rows].join('\n');
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

/**
 * Export data to Excel file with automatic download
 */
export const exportToExcel = async <T extends Record<string, any>>(
  data: T[],
  columns: ExportColumn<T>[],
  filename: string,
  sheetName: string = 'Data'
): Promise<void> => {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  const exportData = prepareExportData(data, columns);

  // Create workbook and worksheet
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);

  // Set up columns with headers and auto-width
  const maxWidth = 50;
  worksheet.columns = columns.map(col => {
    const headerLen = col.header.length;
    const maxDataLen = Math.max(
      ...exportData.map(row => String(row[col.header] || '').length)
    );
    return {
      header: col.header,
      key: col.header,
      width: Math.min(Math.max(headerLen, maxDataLen) + 2, maxWidth)
    };
  });

  // Add data rows
  exportData.forEach(row => {
    worksheet.addRow(row);
  });

  // Generate buffer and trigger download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`;
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

/**
 * Format date for export
 */
export const formatDateForExport = (date: string | Date | null): string => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
};

/**
 * Format subscription tier for display
 */
export const formatSubscriptionTier = (tier: string | null): string => {
  if (!tier) return 'Free';
  return tier.charAt(0).toUpperCase() + tier.slice(1);
};
